import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Globe, ChevronDown, CheckCircle2, Menu, X, Linkedin, Mail, Building2, Users, Target, Award } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { useCountry } from '../hooks/useCountry';
import { SEO } from '../components/SEO';
export function AboutPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const {
    country,
    setCountry
  } = useCountry();
  const isRTL = language === 'ar';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const handleLanguageChange = (newLanguage: 'ar' | 'en') => {
    setLanguage(newLanguage);
    setLanguageDropdownOpen(false);
    window.location.reload();
  };
  const handleCountryChange = (newCountry: 'eg' | 'ae' | 'sa') => {
    setCountry(newCountry);
    setCountryDropdownOpen(false);
    window.location.reload();
  };
  const team = [{
    name: isRTL ? 'عباس الدنيني' : 'Abbas ELDeniney',
    role: isRTL ? 'المؤسس والمدير التنفيذي' : 'Founder & CEO',
    badge: isRTL ? '🇦🇪 المؤسس' : '🇦🇪 Founder',
    image: 'https://cdn.magicpatterns.com/uploads/oVUdaZYUXPDLC2HaCm1QXY/575508456_4144413935806342_83984.png',
    imagePosition: 'object-top',
    desc: isRTL ? 'قائد ذو رؤية استراتيجية في مجال التكنولوجيا والأعمال، يقود Operines نحو الابتكار والنمو في الإمارات والعالم العربي.' : 'A visionary leader in technology and business, driving Operines towards innovation and growth across the UAE and Arab world.',
    social: {
      linkedin: 'https://www.linkedin.com/in/eldeniney'
    },
    gradient: 'from-purple-600 to-pink-600'
  }, {
    name: isRTL ? 'سلطان السويدي' : 'Sultan Alsuwaidi',
    role: isRTL ? 'الشريك والمستثمر الإماراتي' : 'UAE Partner & Investor',
    badge: isRTL ? '🇦🇪 مستثمر' : '🇦🇪 Investor',
    image: 'https://cdn.magicpatterns.com/uploads/tHSkLvNC8786mcqzZ6WCwD/470743797_610009411707896_493400.webp',
    imagePosition: 'object-top',
    desc: isRTL ? 'رجل أعمال ومستثمر إماراتي استراتيجي، يساهم في توسيع نطاق Operines في منطقة الخليج والشرق الأوسط.' : 'Strategic Emirati businessman and investor, contributing to Operines expansion across the Gulf and Middle East.',
    social: {
      linkedin: 'https://www.linkedin.com/in/sultan-alsuwaidi-20192868'
    },
    gradient: 'from-blue-600 to-cyan-500'
  }, {
    name: isRTL ? 'وائل الدنيني' : 'Wael Eldeniney',
    role: isRTL ? 'مطور النظام والمهندس الرئيسي' : 'Lead Developer & Architect',
    badge: isRTL ? '⚡ مطور' : '⚡ Developer',
    image: 'https://cdn.magicpatterns.com/uploads/8r9wafYPSVxpoFNgPNL38G/WhatsApp_Image_2026-02-22_at_2.15.29_AM.jpg',
    imagePosition: 'object-top',
    desc: isRTL ? 'مطور حلول Automation وAI، العقل المدبر خلف تقنيات Operines المتطورة والبنية التقنية للمنصة.' : 'Automation & AI Solutions Developer, the mastermind behind Operines advanced technologies and platform architecture.',
    social: {
      linkedin: 'https://www.linkedin.com/in/wael-eldeniney-3ab6802a9',
      tiktok: 'https://www.tiktok.com/@waeleldeniney0'
    },
    gradient: 'from-emerald-500 to-teal-600'
  }];
  return <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-614">
      <SEO title={isRTL ? 'من نحن - فريق Operines' : 'About Us - Operines Team'} description={isRTL ? 'تعرف على فريق Operines، المنصة الرائدة في أتمتة واتساب بالذكاء الاصطناعي. فريق من الخبراء في مصر والإمارات يقودون مستقبل الأتمتة.' : 'Meet the Operines team, the leading AI WhatsApp automation platform. A team of experts in Egypt and UAE leading the future of automation.'} canonicalPath="/about" language={language as 'ar' | 'en'} data-id="element-615" />
      {/* Navigation (Reused from NewHomePage) */}
      <motion.header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm" initial={{
      y: -100
    }} animate={{
      y: 0
    }} data-id="element-616">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-617">
          <div className="flex items-center justify-between py-4" data-id="element-618">
            <Link to="/" data-id="element-619">
              <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-620" />
            </Link>

            <nav className="hidden md:flex items-center gap-8" data-id="element-621">
              <Link to="/" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-622">
                {isRTL ? 'الرئيسية' : 'Home'}
              </Link>
              <Link to="/about" className="text-[#4f1964] font-medium transition-colors" data-id="element-623">
                {isRTL ? 'من نحن' : 'About Us'}
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-624">
                {isRTL ? 'المدونة' : 'Blog'}
              </Link>
              <Link to="/#pricing" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-625">
                {isRTL ? 'الأسعار' : 'Pricing'}
              </Link>

              {/* Language Selector */}
              <div className="relative" data-id="element-626">
                <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-1 p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-627">
                  <Globe className="w-5 h-5 text-gray-600" data-id="element-628" />
                  <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} data-id="element-629" />
                </button>
                <AnimatePresence data-id="element-630">
                  {languageDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[140px]" data-id="element-631">
                      <button onClick={() => handleLanguageChange('en')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'en' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-632">
                        <span className="text-lg" data-id="element-633">🇺🇸</span>
                        <span data-id="element-634">English</span>
                        {language === 'en' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-635" />}
                      </button>
                      <button onClick={() => handleLanguageChange('ar')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'ar' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-636">
                        <span className="text-lg" data-id="element-637">🇸🇦</span>
                        <span data-id="element-638">العربية</span>
                        {language === 'ar' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-639" />}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Country Selector */}
              <div className="relative" data-id="element-640">
                <button onClick={() => setCountryDropdownOpen(!countryDropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-641">
                  <span className="text-lg" data-id="element-642">
                    {country === 'eg' ? '🇪🇬' : country === 'ae' ? '🇦🇪' : '🇸🇦'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} data-id="element-643" />
                </button>
                <AnimatePresence data-id="element-644">
                  {countryDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[180px]" data-id="element-645">
                      <button onClick={() => handleCountryChange('eg')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'eg' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-646">
                        <span className="text-2xl" data-id="element-647">🇪🇬</span>
                        <div className="text-left" data-id="element-648">
                          <div className="font-semibold" data-id="element-649">
                            {isRTL ? 'مصر' : 'Egypt'}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => handleCountryChange('ae')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'ae' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-650">
                        <span className="text-2xl" data-id="element-651">🇦🇪</span>
                        <div className="text-left" data-id="element-652">
                          <div className="font-semibold" data-id="element-653">
                            {isRTL ? 'الإمارات' : 'UAE'}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => handleCountryChange('sa')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'sa' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-654">
                        <span className="text-2xl" data-id="element-655">🇸🇦</span>
                        <div className="text-left" data-id="element-656">
                          <div className="font-semibold" data-id="element-657">
                            {isRTL ? 'السعودية' : 'Saudi'}
                          </div>
                        </div>
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-600 hover:text-[#4f1964]" data-id="element-658">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-659" /> : <Menu className="w-6 h-6" data-id="element-660" />}
            </button>

            <div className="hidden md:flex items-center gap-3" data-id="element-661">
              <Link to="/login" data-id="element-662">
                <Button variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-663">
                  {isRTL ? 'تسجيل الدخول' : 'Login'}
                </Button>
              </Link>
              <Link to="/register" data-id="element-664">
                <Button size="sm" className="bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-665">
                  {isRTL ? 'ابدأ الآن' : 'Start Now'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-purple-50 to-white relative overflow-hidden" data-id="element-666">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-667">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-3xl mx-auto" data-id="element-668">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-669">
              {isRTL ? 'من نحن' : 'Who We Are'}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-670">
              {isRTL ? 'نحن في Operines نؤمن بقوة الأتمتة والذكاء الاصطناعي في تحويل طريقة تواصل الشركات مع عملائها. هدفنا هو جعل التكنولوجيا المتقدمة في متناول الجميع.' : 'At Operines, we believe in the power of automation and AI to transform how businesses connect with their customers. Our goal is to make advanced technology accessible to everyone.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white" data-id="element-671">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-672">
          <div className="grid md:grid-cols-3 gap-8" data-id="element-673">
            {[{
            icon: Target,
            title: isRTL ? 'مهمتنا' : 'Our Mission',
            desc: isRTL ? 'تمكين الشركات من أتمتة تواصلها وزيادة مبيعاتها عبر واتساب باستخدام أحدث تقنيات الذكاء الاصطناعي.' : 'Empowering businesses to automate communication and boost sales via WhatsApp using cutting-edge AI technologies.'
          }, {
            icon: Award,
            title: isRTL ? 'رؤيتنا' : 'Our Vision',
            desc: isRTL ? 'أن نكون المنصة الرائدة عالمياً في مجال أتمتة المحادثات التجارية، ونقدم حلولاً مبتكرة تناسب جميع الأحجام.' : 'To be the leading global platform for business conversation automation, providing innovative solutions for all sizes.'
          }, {
            icon: Users,
            title: isRTL ? 'قيمنا' : 'Our Values',
            desc: isRTL ? 'الابتكار، الشفافية، والتركيز على نجاح العميل هي الركائز الأساسية التي نبني عليها كل ما نقوم به.' : 'Innovation, transparency, and customer success are the core pillars upon which we build everything we do.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="p-8 rounded-2xl bg-gray-50 border border-gray-100 text-center hover:shadow-lg transition-shadow" data-id="element-674">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-675">
                  <item.icon className="w-8 h-8 text-[#4f1964]" data-id="element-676" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-677">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-678">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-b from-white to-purple-50" data-id="element-679">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-680">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-681">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-682">
              {isRTL ? 'فريق القيادة' : 'Leadership Team'}
            </h2>
            <p className="text-gray-600" data-id="element-683">
              {isRTL ? 'نخبة من الخبراء يقودون مستقبل الأتمتة' : 'Experts leading the future of automation'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-684">
            {team.map((member, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2,
            type: 'spring',
            stiffness: 150
          }} whileHover={{
            y: -8
          }} className="bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100 group" data-id="element-685">
                {/* Photo area - tall to show full/upper body */}
                <div className="relative h-80 overflow-hidden bg-gray-100" data-id="element-686">
                  {/* Gradient overlay at bottom */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent z-10" data-id="element-687" />

                  {/* Badge */}
                  <div className="absolute top-4 left-4 z-20 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-gray-800 shadow-sm" data-id="element-688">
                    {member.badge}
                  </div>

                  <img src={member.image} alt={member.name} className={`w-full h-full object-cover ${member.imagePosition} transition-transform duration-700 group-hover:scale-105`} data-id="element-689" />

                  {/* Name overlay on photo */}
                  <div className="absolute bottom-0 left-0 right-0 z-20 p-5" data-id="element-690">
                    <h3 className="text-xl font-bold text-white mb-0.5" data-id="element-691">
                      {member.name}
                    </h3>
                    <div className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${member.gradient}`} data-id="element-692">
                      {member.role}
                    </div>
                  </div>
                </div>

                {/* Content below photo */}
                <div className="p-6" data-id="element-693">
                  <p className="text-gray-600 text-sm leading-relaxed mb-5" data-id="element-694">
                    {member.desc}
                  </p>

                  <div className="flex gap-2" data-id="element-695">
                    {member.social.linkedin && <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0077b5]/10 text-[#0077b5] rounded-lg text-xs font-medium hover:bg-[#0077b5] hover:text-white transition-all" data-id="element-696">
                        <Linkedin className="w-3.5 h-3.5" data-id="element-697" />
                        LinkedIn
                      </a>}
                    {member.social.tiktok && <a href={member.social.tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-black hover:text-white transition-all" data-id="element-698">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-699">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" data-id="element-700" />
                        </svg>
                        TikTok
                      </a>}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer (Reused) */}
      <footer className="bg-[#4f1964] text-white py-12" data-id="element-701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-702">
          <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-703">
            <div data-id="element-704">
              <span className="text-2xl font-bold text-white mb-4 block" data-id="element-705">
                operines
              </span>
              <p className="text-purple-200 text-sm" data-id="element-706">
                {isRTL ? 'أتمتة واتساب بالذكاء الاصطناعي' : 'AI-Powered WhatsApp Automation'}
              </p>
            </div>
            <div data-id="element-707">
              <h4 className="font-bold mb-4" data-id="element-708">
                {isRTL ? 'روابط سريعة' : 'Quick Links'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-709">
                <li data-id="element-710">
                  <Link to="/" className="hover:text-white transition-colors" data-id="element-711">
                    {isRTL ? 'الرئيسية' : 'Home'}
                  </Link>
                </li>
                <li data-id="element-712">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-713">
                    {isRTL ? 'من نحن' : 'About Us'}
                  </Link>
                </li>
                <li data-id="element-714">
                  <Link to="/blog" className="hover:text-white transition-colors" data-id="element-715">
                    {isRTL ? 'المدونة' : 'Blog'}
                  </Link>
                </li>
                <li data-id="element-716">
                  <Link to="/#pricing" className="hover:text-white transition-colors" data-id="element-717">
                    {isRTL ? 'الأسعار' : 'Pricing'}
                  </Link>
                </li>
              </ul>
            </div>
            <div data-id="element-718">
              <h4 className="font-bold mb-4" data-id="element-719">
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-720">
                <li data-id="element-721">hello@operines.ai</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-purple-800 pt-8 text-center text-purple-200 text-sm" data-id="element-722">
            © {new Date().getFullYear()} Operines.{' '}
            {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </div>
        </div>
      </footer>
    </div>;
}